import React from 'react';
import { motion } from 'framer-motion';
import { TargetIcon, EyeIcon, CheckCircle2Icon, BuildingIcon } from 'lucide-react';
import { PageShell } from '../components/layout/PageShell';
import { GlassCard } from '../components/ui/GlassCard';
import { SmartImage } from '../components/common/SmartImage';
import { useI18n } from '../lib/i18n';
import { IMAGES } from '../lib/images';
export function About() {
  const {
    t
  } = useI18n();
  const objectives = ['obj_1', 'obj_2', 'obj_3', 'obj_4', 'obj_5'];
  return <PageShell data-id="element-161" data-mp="376cgi5z9qb7c">
      <section className="mx-auto max-w-container px-4 py-12 sm:px-6" data-id="element-162" data-mp="376vnwsralh74">
        <div className="flex flex-col items-center text-center" data-id="element-163" data-mp="2sawhdmjtzp43">
          <SmartImage src={IMAGES.companyLogo} alt="SATEV Group logo" className="h-16 w-16 rounded-lg object-contain" data-id="element-164" data-mp="2x97se0lv45nw" />
          <h1 className="mt-4 text-3xl font-extrabold text-white sm:text-4xl" data-id="element-165" data-mp="1gteq3mjfshyg">
            {t('about_title')}
          </h1>
          <p className="mt-2 text-silver/70" data-id="element-166" data-mp="1lrokhuxc7ej0">{t('tagline_company')}</p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3" data-id="element-167" data-mp="1ujw6jejebg8z">
          <GlassCard className="lg:col-span-2" data-id="element-168" data-mp="1pjqmokjday1g">
            <div className="mb-3 flex items-center gap-2" data-id="element-169" data-mp="1pl1q2j8wi3s1">
              <BuildingIcon className="h-5 w-5 text-neon" data-id="element-170" data-mp="23j9icduzsgwv" />
              <h2 className="text-xl font-semibold text-white" data-id="element-171" data-mp="1yjn5i5j2t2ol">
                {t('company_overview')}
              </h2>
            </div>
            <p className="leading-relaxed text-silver/80" data-id="element-172" data-mp="2dfcumcjk5vxj">
              {t('company_overview_body')}
            </p>
          </GlassCard>

          <GlassCard className="overflow-hidden p-0" data-id="element-173" data-mp="28fqg7zrzu9n5">
            <SmartImage src={IMAGES.productImage} alt="RevoV machine" className="h-full min-h-[220px] w-full object-cover" data-id="element-174" data-mp="2m7o7swpnvmvk" />
          </GlassCard>
        </div>

        <div className="mt-6 grid gap-6 md:grid-cols-2" data-id="element-175" data-mp="2w7nf6qach76u">
          <GlassCard data-id="element-176" data-mp="2r8klg1ztene6">
            <div className="mb-3 flex items-center gap-2" data-id="element-177" data-mp="2r93wqzuymdj8">
              <TargetIcon className="h-5 w-5 text-neon" data-id="element-178" data-mp="1oumojreqrgkj" />
              <h2 className="text-xl font-semibold text-white" data-id="element-179" data-mp="1ow7j3h122zie">
                {t('mission')}
              </h2>
            </div>
            <p className="leading-relaxed text-silver/80" data-id="element-180" data-mp="1xvb62w3cfz26">
              {t('mission_body')}
            </p>
          </GlassCard>
          <GlassCard data-id="element-181" data-mp="22mb2m08jwf45">
            <div className="mb-3 flex items-center gap-2" data-id="element-182" data-mp="22mt69hxjlvm3">
              <EyeIcon className="h-5 w-5 text-neon" data-id="element-183" data-mp="27le6hhcsofaj" />
              <h2 className="text-xl font-semibold text-white" data-id="element-184" data-mp="27kuqi66pg0z5">
                {t('vision')}
              </h2>
            </div>
            <p className="leading-relaxed text-silver/80" data-id="element-185" data-mp="2glsvorsvqejb">{t('vision_body')}</p>
          </GlassCard>
        </div>

        <GlassCard className="mt-6" data-id="element-186" data-mp="2lkng1y6hwb5j">
          <h2 className="mb-4 text-xl font-semibold text-white" data-id="element-187" data-mp="2ljlxl8n5gmds">
            {t('objectives')}
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2" data-id="element-188" data-mp="m9ckdcl88a7l">
            {objectives.map((key, i) => <motion.li key={key} initial={{
            opacity: 0,
            x: -12
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.06
          }} className="flex items-start gap-3 text-silver/80" data-id="element-189" data-mp="r7e8ibxiezpx" data-mpi={i}>
                <CheckCircle2Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-neon" data-id="element-190" data-mp="12lg0c8u744iy" />
                <span data-id="element-191" data-mp="xofqrny50jn3">{t(key)}</span>
              </motion.li>)}
          </ul>
        </GlassCard>

        <div className="mt-6" data-id="element-192" data-mp="smgroq9fk8vy">
          <h2 className="mb-4 text-xl font-semibold text-white" data-id="element-193" data-mp="omgch3eibtnu">
            {t('the_product')}
          </h2>
          <GlassCard className="flex flex-col items-center gap-6 sm:flex-row" data-id="element-194" data-mp="3de0qofdkziro">
            <SmartImage src={IMAGES.productLogo} alt="RevoV" className="h-16 w-auto object-contain" data-id="element-195" data-mp="3dbxqx3mmmb9r" />
            <p className="text-silver/80" data-id="element-196" data-mp="1137brwsmk0op">
              RevoV — {t('hero_desc')} 24/7 · IoT-monitored · cashless ·
              modular.
            </p>
          </GlassCard>
        </div>
      </section>
    </PageShell>;
}